import React from 'react';
import { Stack, Surface, Inline } from '@packages/ui/layout';
import type { FormEachNode } from '../../types/layout';
import { FormRowExcludedValuesProvider } from '../../context/FormRowContext';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { useFormEachArray } from '../../hooks/useFormEachArray';
import { useArrayEffectProcessor } from '../../processors/arrayEffectProcessor';
import { resolveAddButtonPosition } from '../../utils/resolveAddButtonPosition';
import { buildExcludedSiblingValues } from './each/buildExcludedSiblingValues';
import { FormEachAddButton } from './each/FormEachAddButton';
import { FormEachRemoveButton } from './each/FormEachRemoveButton';
import { FormEachResetButton } from './each/FormEachResetButton';
import { FormEachRowHeader } from './each/FormEachRowHeader';
import { FormEachItemFields } from './each/FormEachItemFields';

interface FormEachCardRendererProps {
  node: FormEachNode;
}

export const FormEachCardRenderer = React.memo(function FormEachCardRenderer({
  node,
}: FormEachCardRendererProps): React.ReactElement {
  const { form, hydrationEpochRef } = useFormEngineForm();
  const { meta, getOptionProp } = useFormEngineState();

  useArrayEffectProcessor({
    form: form as never,
    arrayEffects: node.arrayEffects,
    arrayName: node.name,
    meta,
    getOptionProp,
    hydrationEpochRef,
  });

  const {
    fields,
    handleAdd,
    handleRemove,
    showAdd,
    canRemoveRow,
    allRowValues,
    siblingExcludeFields,
  } = useFormEachArray(node);

  const columns = typeof node.columns === 'number' || typeof node.columns === 'string'
    ? node.columns
    : (node.columns ?? 2);

  const addPosition = resolveAddButtonPosition(node);
  const addControl = showAdd ? <FormEachAddButton node={node} onClick={handleAdd} /> : null;

  return (
    <Stack gap='var(--ui-space-md, 16px)'>
      {addPosition === 'top-right' && addControl != null && (
        <Inline justify='end'>{addControl}</Inline>
      )}

      {fields.map((item, index) => {
        const excludedValues = buildExcludedSiblingValues(allRowValues, index, siblingExcludeFields);
        const rowValues = allRowValues?.[index] as Record<string, unknown> | undefined;
        const showRemove = canRemoveRow();
        const removeInHeader = node.removeButton?.position !== 'row-end';

        return (
          <FormRowExcludedValuesProvider key={item.id} value={excludedValues}>
            <Surface bordered padding='16px'>
              <Stack gap='12px'>
                <Inline justify='between' align='center'>
                  <FormEachRowHeader config={node.rowHeader} index={index} rowValues={rowValues} />
                  {showRemove && removeInHeader && (
                    <FormEachRemoveButton node={node} index={index} onRemove={handleRemove} />
                  )}
                </Inline>

                <FormEachItemFields
                  node={node}
                  index={index}
                  total={fields.length}
                  columns={columns}
                  itemClassName={node.itemClassName}
                />

                {(showRemove || node.resetButton != null) && node.removeButton?.position === 'row-end' && (
                  <Inline justify='end' gap='8px'>
                    {node.resetButton != null && <FormEachResetButton node={node} index={index} />}
                    {showRemove && <FormEachRemoveButton node={node} index={index} onRemove={handleRemove} />}
                  </Inline>
                )}
              </Stack>
            </Surface>
          </FormRowExcludedValuesProvider>
        );
      })}

      {addPosition !== 'top-right' && addControl}
    </Stack>
  );
});
